import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, ButtonDropdown, Grid, SpaceBetween, Spinner } from "@amzn/awsui-components-react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as d3 from "d3";
import { configureFlexicoS3, configureFriscoS3, configureGenericS3 } from 'src/context/AuthContextUtility';
import { getDocumentFromVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from "src/Ateam-BI/Vault/constants";
import { cleanS3PathForDownload, decodeS3Key } from 'src/utilities/S3Utils';
import EmbedExcel from './EmbedExcel';
import EmbedWord from './EmbedWord';
import EmbedImage from './EmbedImage';

/**
 * EmbedSingleFile - Renders a single file inline without tabs.
 * Fetches the presigned URL from vault and renders based on file type.
 * For PDFs, fetches as blob to avoid Content-Disposition: attachment.
 *
 * Props:
 *   filePath (string) - S3 file path
 *   bucket (string) - bucket name (used to determine frisco vs flexico)
 *   serviceId (string) - service identifier for vault
 *   appId (string) - app identifier for vault
 *   service (string) - optional override: 'FRISCO', 'FLEXICO', or 'TANC'
 */
const EmbedSingleFile = ({ filePath, bucket, serviceId, appId, service: serviceProp }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // PDF state
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

    // Excel state
    const [excelFileUrl, setExcelFileUrl] = useState(null);
    const [excelFileName, setExcelFileName] = useState(null);

    // Word state
    const [wordFileUrl, setWordFileUrl] = useState(null);
    const [wordFileName, setWordFileName] = useState(null);

    // Image state
    const [imageFileUrl, setImageFileUrl] = useState(null);
    const [imageFileName, setImageFileName] = useState(null);

    // CSV state
    const [dsInfo, setDsInfo] = useState(null);
    const [columnsList, setColumnsList] = useState([]);
    const [gridInfo, setGridInfo] = useState({ rowCount: 0, totalRows: 0 });

    const gridRef = useRef();
    const dsInfoRef = useRef(null);

    const ext = (filePath || '').split('.').pop().toLowerCase();
    const fileName = (filePath || '').split('/').pop() || 'File';

    // Keep dsInfo ref in sync for stable callbacks
    useEffect(() => { dsInfoRef.current = dsInfo; }, [dsInfo]);

    useEffect(() => {
        let cancelled = false;
        let revoke = null;
        const loadFile = async () => {
            setLoading(true);
            setError(null);
            setPdfBlobUrl(null);
            setExcelFileUrl(null);
            setWordFileUrl(null);
            setImageFileUrl(null);
            setDsInfo(null);
            setColumnsList([]);

            try {
                // Determine service
                let vaultService = serviceProp;
                if (!vaultService) {
                    vaultService = (bucket && bucket.endsWith('frisco'))
                        ? VAULT_SERVICES.FRISCO
                        : VAULT_SERVICES.FLEXICO;
                }

                // Configure S3 based on resolved service
                if (vaultService === VAULT_SERVICES.FRISCO) {
                    configureFriscoS3();
                } else {
                    configureFlexicoS3();
                }

                const fn = cleanS3PathForDownload(filePath);
                const serviceParams = { file_path: `${decodeS3Key(fn).replaceAll('ABCPLUSTEMP', '+')}` };
                if (serviceId) serviceParams.service_id = `${serviceId}`;
                if (appId) serviceParams.app_id = `${appId}`;

                const document = await getDocumentFromVault(ext, vaultService, serviceParams);
                if (cancelled) return;
                if (!document?.fileUrl) {
                    setError('Could not retrieve file URL.');
                    configureGenericS3();
                    return;
                }

                const fileUrl = document.fileUrl;

                if (ext === 'pdf') {
                    // Fetch as blob to bypass Content-Disposition: attachment
                    const response = await fetch(fileUrl);
                    if (cancelled) return;
                    if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
                    const blob = await response.blob();
                    if (cancelled) return;
                    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    revoke = url;
                    setPdfBlobUrl(url);
                } else if (['xlsx', 'xls'].includes(ext)) {
                    setExcelFileUrl(fileUrl);
                    setExcelFileName(fileName);
                } else if (['docx', 'doc'].includes(ext)) {
                    setWordFileUrl(fileUrl);
                    setWordFileName(fileName);
                } else if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif'].includes(ext)) {
                    setImageFileUrl(fileUrl);
                    setImageFileName(fileName);
                } else {
                    // CSV and other text-based files
                    await d3.csv(fileUrl).then((result) => {
                        if (cancelled) return;
                        dsInfoRef.current = result;
                        setDsInfo(result);
                        if (result?.columns) {
                            const definitions = result.columns.map((colName, i) => {
                                const def = {
                                    field: colName,
                                    headerName: colName,
                                    resizable: true,
                                    sortable: true,
                                    enableRowGroup: true,
                                    editable: false,
                                    cellSelection: 'enabled',
                                };
                                if (i === 0) {
                                    def.checkboxSelection = true;
                                    def.headerCheckboxSelection = true;
                                }
                                return def;
                            });
                            setColumnsList(definitions);
                        }
                    }).catch(() => {
                        if (!cancelled) setError('Failed to load CSV file.');
                    });
                }

                configureGenericS3();
            } catch (e) {
                if (!cancelled) {
                    console.error('EmbedSingleFile error:', e);
                    setError('Failed to load file.');
                }
                configureGenericS3();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        if (filePath) loadFile();
        return () => {
            cancelled = true;
            if (revoke) URL.revokeObjectURL(revoke);
        };
    }, [filePath, bucket, serviceId, appId, serviceProp]);

    // Grid helpers
    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true,
        filterParams: { applyMiniFilterWhileTyping: true },
        enableCellChangeFlash: true,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        cellSelection: 'single',
    };

    const sideBar = {
        toolPanels: [
            { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
            { id: 'filters', labelDefault: 'Filters', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
        ],
        defaultToolPanel: '',
    };

    const exportAnnotated = (args = {}) => {
        gridRef.current?.api?.exportDataAsCsv({
            fileName: fileName || 'output.csv',
            exportedRows: args.filtered ? 'filteredAndSorted' : 'all',
            onlySelected: args.selectedOnly,
            processCellCallback: (params) => params.node.data[params.column.colDef.field],
        });
    };

    const getContextMenuItems = () => ['copy', 'copyWithHeaders', 'separator', 'export'];

    const updateGridInfo = useCallback(() => {
        if (gridRef?.current?.api) {
            setGridInfo({
                rowCount: gridRef.current.api.getDisplayedRowCount(),
                totalRows: dsInfoRef.current?.length,
            });
        }
    }, []);

    const onFilterChanged = () => updateGridInfo();
    const onViewportChanged = () => updateGridInfo();

    const onGridReady = useCallback(() => {
        const allColumnIds = [];
        gridRef?.current?.api?.getColumns()?.forEach((column) => allColumnIds.push(column.getId()));
        gridRef?.current?.api?.autoSizeColumns(allColumnIds);
        updateGridInfo();
    }, []);

    if (loading) return <Box padding="l"><Spinner /> Loading file...</Box>;
    if (error) return <Box padding="l">{error}</Box>;

    // PDF
    if (pdfBlobUrl) {
        return (
            <iframe
                src={pdfBlobUrl}
                style={{ width: '100%', height: '700px', border: '1px solid #d5dbdb', borderRadius: '4px' }}
                title="PDF Viewer"
            />
        );
    }

    // Excel
    if (excelFileUrl) {
        return <EmbedExcel fileUrl={excelFileUrl} fileName={excelFileName || 'output.xlsx'} />;
    }

    // Word
    if (wordFileUrl) {
        return <EmbedWord fileUrl={wordFileUrl} fileName={wordFileName || 'document.docx'} />;
    }

    // Image
    if (imageFileUrl) {
        return <EmbedImage fileUrl={imageFileUrl} fileName={imageFileName || 'image'} />;
    }

    // CSV / text grid
    if (dsInfo) {
        return (
            <SpaceBetween direction="vertical">
                <Grid gridDefinition={[{ colspan: { default: 6 } }, { colspan: { default: 6 } }]}>
                    <Box />
                    <Box float="right" padding="m">
                        <ButtonDropdown
                            items={[{
                                text: "Export to excel",
                                items: [
                                    { text: "Current view", id: "current_view" },
                                    { text: "Selected rows", id: "selected_rows" },
                                    { text: "Original", id: "original" },
                                ]
                            }]}
                            onItemClick={(event) => {
                                if (event.detail.id === 'current_view') exportAnnotated({ filtered: true });
                                else if (event.detail.id === 'selected_rows') exportAnnotated({ selectedOnly: true });
                                else if (event.detail.id === 'original') exportAnnotated();
                            }}
                        >
                            Actions
                        </ButtonDropdown>
                    </Box>
                </Grid>
                <div style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                    <div className="ag-theme-alpine" style={{ width: '100%', height: '100%', minHeight: '500px' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={dsInfo}
                            defaultColDef={defaultColDef}
                            columnDefs={columnsList}
                            rowSelection="multiple"
                            suppressRowClickSelection={true}
                            rowMultiSelectWithClick={true}
                            animateRows={true}
                            rowHeight={30}
                            enableCellTextSelection={false}
                            onGridReady={onGridReady}
                            rowGroupPanelShow="always"
                            sideBar={sideBar}
                            enableRangeSelection={true}
                            getContextMenuItems={getContextMenuItems}
                            onFilterChanged={onFilterChanged}
                            onViewportChanged={onViewportChanged}
                        />
                        {gridInfo && (
                            <Box float="left">
                                <SpaceBetween direction="horizontal" size="s">
                                    <Box><span className="agg-text bold-text">Filtered rows:</span> <span className="agg-text">{gridInfo.rowCount}</span></Box>
                                    <Box><span className="agg-text bold-text">Total rows:</span> <span className="agg-text">{gridInfo.totalRows}</span></Box>
                                </SpaceBetween>
                            </Box>
                        )}
                    </div>
                </div>
            </SpaceBetween>
        );
    }

    return <Box padding="l">Unable to display this file type.</Box>;
};

export default EmbedSingleFile;
